import { tz } from '@date-fns/tz';
import { format } from 'date-fns';
import { styled } from 'styled-components';

import type {
  ApiISODateString,
  ApiISODateTimeNoTzString,
  ApiISODateTimeString,
} from '#/api/types/ApiTypedDate';
import { Box } from '#/components/Box/Box';
import { DefaultButton as TooltipButton } from '#/components/LegacyTooltip/LegacyTooltip.styled';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors } from '#/theme/theme';
import {
  DATE_FNS_LOCALE_BY_LOCALE,
  getDate,
  humanDate,
  shorterTime,
} from '#/utils/date';
import { getUserTimezone } from '#/utils/timezones';

const StyledTooltipButton = styled(TooltipButton)`
  color: ${colors.lightContentGrey};

  &:hover {
    color: ${colors.brandBlue};
  }
`;

type TimeTooltipProps = {
  startDateTime: string;
  endDateTime?: string;
  timezone: string | null | undefined;
};

export function TimeTooltip({
  startDateTime,
  endDateTime,
  timezone,
}: TimeTooltipProps) {
  const { locale } = useI18n();
  const startTime = shorterTime({
    date: startDateTime,
    locale,
    timezone,
    displayTimezone: !endDateTime,
  });
  const endTime = endDateTime
    ? shorterTime({
        date: endDateTime,
        locale,
        timezone,
        displayTimezone: true,
      })
    : '';
  return (
    <Box ml="8px" mr="4px">
      <Tooltip
        variant="filled"
        content={
          <>
            {startTime}
            {endTime ? ' - ' : ''}
            {endTime}
          </>
        }
      >
        <StyledTooltipButton />
      </Tooltip>
    </Box>
  );
}

type SingleDayDateTimeProps = {
  startDate: ApiISODateString | ApiISODateTimeNoTzString | ApiISODateTimeString;
  endDate: ApiISODateString | ApiISODateTimeNoTzString | ApiISODateTimeString;
  showStartTime?: boolean;
  showEndTime?: boolean;
  timezone?: string | null;
  qaId?: string;
};

export function SingleDayDateTime({
  startDate,
  endDate,
  showStartTime,
  showEndTime,
  timezone,
  qaId,
}: SingleDayDateTimeProps) {
  const { locale } = useI18n();
  const startDateTime = shorterTime({
    date: startDate,
    locale,
    timezone,
    displayTimezone: !endDate,
  });
  const endDateTime = endDate
    ? shorterTime({
        date: endDate,
        locale,
        timezone,
        displayTimezone: true,
      })
    : '';
  const userTimezone = getUserTimezone();
  return (
    <div
      data-qa-id={qaId}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <div>{humanDate({ date: startDate, locale, timezone })}</div>
      {showStartTime && showEndTime && (
        <Box display="flex" alignItems="center">
          <>
            {startDateTime}
            {showEndTime ? ' - ' : ''}
            {endDateTime}
          </>
          {userTimezone !== timezone && (
            <TimeTooltip
              startDateTime={startDate}
              endDateTime={endDate}
              timezone={userTimezone}
            />
          )}
        </Box>
      )}
    </div>
  );
}

type DateTimeProps = {
  dateTime: ApiISODateTimeNoTzString | ApiISODateTimeString;
  timezone: string | null | undefined;
  qaId?: string;
};

export function FormattedDateTime({ dateTime, timezone, qaId }: DateTimeProps) {
  const { locale } = useI18n();
  const userTimezone = getUserTimezone();

  return (
    <div data-qa-id={qaId}>
      <div style={{ display: 'inline-block' }}>
        {timezone
          ? format(dateTime, 'MMMM d, yyyy, h:mm a zzz', {
              in: tz(timezone),
              locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
            })
          : format(getDate(dateTime), 'MMMM d, yyyy, h:mm a', {
              locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
            })}
      </div>

      {userTimezone !== timezone && (
        <div style={{ display: 'inline-block', marginLeft: '4px' }}>
          <TimeTooltip startDateTime={dateTime} timezone={userTimezone} />
        </div>
      )}
    </div>
  );
}

type DateProps = {
  date: ApiISODateString;
  qaId?: string;
};

export function FormattedDate({ date, qaId }: DateProps) {
  const { locale } = useI18n();
  return (
    <div data-qa-id={qaId}>
      <div style={{ display: 'inline-block' }}>
        {format(getDate(date), 'MMMM d, yyyy', {
          locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
        })}
      </div>
    </div>
  );
}
